/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Link } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

import { buildPaginationRange } from './pagination-range'

export interface PaginationControlProps {
  page: number
  count: number
  pageSize: number
  onPageChange?: (details: { page: number }) => void
  /** @default 1 */
  siblingCount?: number
  /** Left-aligned summary or custom controls (e.g. result counts). */
  context?: ReactNode
  /** @default true */
  showSeparator?: boolean
  /** Props for the left context region. */
  contextContainerProps?: ComponentProps<'div'>
  /** Props for the outer wrapper (full width). */
  rootContainerProps?: ComponentProps<'div'>
  /** Aria-label for the previous page control. */
  previousPageAriaLabel?: string
  /** Aria-label for the next page control. */
  nextPageAriaLabel?: string
}

function PaginationPages({
  totalPages,
  page,
  siblingCount,
  previousPageAriaLabel,
  nextPageAriaLabel,
}: {
  totalPages: number
  page: number
  siblingCount: number
  previousPageAriaLabel: string
  nextPageAriaLabel: string
}) {
  const { t } = useTranslation()
  const range = buildPaginationRange(totalPages, page, siblingCount)

  const linkSearch = (nextPage: number) => (prev: Record<string, unknown>) => ({ ...prev, page: nextPage })

  const prevPage = Math.max(1, page - 1)
  const nextPage = Math.min(totalPages, page + 1)

  return (
    <nav aria-label={t('pagination')} className="flex flex-wrap items-center justify-center gap-1 md:gap-2">
      {page <= 1 ? (
        <Button aria-label={previousPageAriaLabel} disabled size="icon" variant="outline">
          <ChevronLeftIcon />
        </Button>
      ) : (
        <Button
          aria-label={previousPageAriaLabel}
          nativeButton={false}
          render={<Link search={linkSearch(prevPage)} to="." />}
          size="icon"
          variant="outline"
        >
          <ChevronLeftIcon />
        </Button>
      )}

      {range.map((entry, index) =>
        entry === 'ellipsis' ? (
          <span
            className="flex size-8 items-center justify-center text-muted-foreground md:size-9"
            key={`ellipsis-${index}`}
          >
            …
          </span>
        ) : (
          <Button
            className={cn('min-w-8 md:min-w-9')}
            key={entry}
            nativeButton={false}
            render={<Link search={linkSearch(entry)} to="." />}
            variant={entry === page ? 'default' : 'outline'}
          >
            {entry}
          </Button>
        )
      )}

      {page >= totalPages ? (
        <Button aria-label={nextPageAriaLabel} disabled size="icon" variant="outline">
          <ChevronRightIcon />
        </Button>
      ) : (
        <Button
          aria-label={nextPageAriaLabel}
          nativeButton={false}
          render={<Link search={linkSearch(nextPage)} to="." />}
          size="icon"
          variant="outline"
        >
          <ChevronRightIcon />
        </Button>
      )}
    </nav>
  )
}

export function PaginationControl({
  page,
  count,
  pageSize,
  siblingCount = 1,
  context,
  showSeparator = true,
  contextContainerProps,
  rootContainerProps,
  previousPageAriaLabel,
  nextPageAriaLabel,
}: PaginationControlProps) {
  const { t } = useTranslation()
  const resolvedPreviousPageAriaLabel = previousPageAriaLabel ?? t('Previous page')
  const resolvedNextPageAriaLabel = nextPageAriaLabel ?? t('Next page')
  const totalPages = Math.max(1, Math.ceil(count / pageSize))

  if (count <= 0) {
    return null
  }

  return (
    <div className={cn('flex w-full flex-col gap-4 pt-8')} {...rootContainerProps}>
      {showSeparator ? <Separator className="w-full" /> : null}
      <div className="flex w-full flex-col items-center justify-between gap-3 py-0 md:flex-row">
        <div className="hidden min-w-0 flex-1 text-start md:block" {...contextContainerProps}>
          {typeof context === 'string' || typeof context === 'number' ? (
            <p className="line-clamp-1 break-all text-muted-foreground text-sm">{context}</p>
          ) : (
            context
          )}
        </div>

        <PaginationPages
          nextPageAriaLabel={resolvedNextPageAriaLabel}
          page={page}
          previousPageAriaLabel={resolvedPreviousPageAriaLabel}
          siblingCount={siblingCount}
          totalPages={totalPages}
        />
      </div>
    </div>
  )
}
